import { ImmutableVector3, getFlatShapeNormal } from '@modugen/ui-toolkit'

/**
 * Fixed id for the clip bracket created from the opening plane list. Because plane selection always
 * replaces existing clip planes, at most one such bracket exists at a time, so a constant id lets
 * the panel recognize when its own bracket is removed (to clear the active highlight).
 */
export const OPENING_ALIGN_BRACKET_ID = 'opening-align-bracket'

export interface DetectedOpeningPlane {
  id: string
  /** Face normal of the walls in this plane (canonical direction). */
  normal: ImmutableVector3
  /** A point that lies on the plane, used to anchor the clip bracket. */
  point: ImmutableVector3
  wallGuids: string[]
  openingCount: number
  /**
   * Framing for looking straight at the plane: in-plane screen axes (`right × up` points at the
   * viewer, i.e. along {@link normal}) and the bounding extents of the plane's walls, centered on
   * `center`. Feeds an `orientedFitPerspective` camera event.
   */
  frame: {
    center: ImmutableVector3
    right: ImmutableVector3
    up: ImmutableVector3
    width: number
    height: number
  }
}

// Walls whose faces lie within this distance of each other count as one plane.
const PLANE_DISTANCE_THRESHOLD = 0.1
// Wall normals are treated as parallel above this dot product (~2.5°).
const PARALLEL_DOT_THRESHOLD = 0.999

interface WorkingPlaneGroup {
  normal: ImmutableVector3
  offset: number
  // Smallest and largest wall offsets in the group, so a wall only joins when the resulting span
  // stays within the threshold (keeps grouping independent of the order walls are visited in).
  minOffset: number
  maxOffset: number
  wallGuids: string[]
  openingCount: number
  centroidSum: ImmutableVector3
  centroidCount: number
  // All shape points of the group's walls, to compute the in-plane framing extents.
  points: ImmutableVector3[]
}

const getShapeCentroid = (points: ImmutableVector3[]): ImmutableVector3 =>
  points
    .reduce((acc, point) => acc.add(point), new ImmutableVector3(0, 0, 0))
    .multiplyScalar(1 / points.length)

// Flip the normal into a canonical direction so a wall and the wall facing it from
// the other side (opposite normals) end up grouped into the same plane.
const canonicalizeNormal = (normal: ImmutableVector3): ImmutableVector3 => {
  const epsilon = 1e-6
  const leadingComponent =
    Math.abs(normal.x) > epsilon ? normal.x : Math.abs(normal.y) > epsilon ? normal.y : normal.z

  return leadingComponent < 0 ? normal.multiplyScalar(-1) : normal
}

/**
 * Groups the walls that have openings into shared planes: any walls whose faces are parallel and
 * lie within {@link PLANE_DISTANCE_THRESHOLD} of each other are reported as a single plane. Each
 * plane carries the wall face normal and a point on the plane so the caller can drop a clip bracket
 * ("Schnittklammer") there to check the openings.
 */
export const detectOpeningPlanes = (walls: ShapeObject[]): DetectedOpeningPlane[] => {
  const groups: WorkingPlaneGroup[] = []

  walls.forEach(wall => {
    const openings = (wall.openings || []).filter(opening => !opening.is_local)
    if (openings.length === 0) return

    const points = wall.shape.points
    if (points.length < 3) return

    const normal = canonicalizeNormal(
      getFlatShapeNormal(
        points as [ImmutableVector3, ImmutableVector3, ImmutableVector3, ...ImmutableVector3[]],
      ).normalize(),
    )
    // Degenerate (collinear) polygons yield a zero-length normal; skip them.
    if (normal.length() < 0.5) return

    const centroid = getShapeCentroid(points)
    const offset = normal.dot(centroid)

    const existing = groups.find(group => {
      if (group.normal.dot(normal) <= PARALLEL_DOT_THRESHOLD) return false
      const candidateOffset = group.normal.dot(centroid)
      const span =
        Math.max(group.maxOffset, candidateOffset) - Math.min(group.minOffset, candidateOffset)
      return span <= PLANE_DISTANCE_THRESHOLD
    })

    if (existing) {
      const candidateOffset = existing.normal.dot(centroid)
      existing.wallGuids.push(wall.guid)
      existing.openingCount += openings.length
      existing.centroidSum = existing.centroidSum.add(centroid)
      existing.centroidCount += 1
      existing.minOffset = Math.min(existing.minOffset, candidateOffset)
      existing.maxOffset = Math.max(existing.maxOffset, candidateOffset)
      existing.points.push(...points)
    } else {
      groups.push({
        normal,
        offset,
        minOffset: offset,
        maxOffset: offset,
        wallGuids: [wall.guid],
        openingCount: openings.length,
        centroidSum: centroid,
        centroidCount: 1,
        points: [...points],
      })
    }
  })

  return (
    groups
      // A plane needs alignment checking only when its openings can be misaligned against each
      // other: with a single wall (which also covers 0/1 openings — every grouped wall has at least
      // one opening) all openings live in the same wall and share its coordinate system.
      .filter(group => group.wallGuids.length >= 2)
      .sort((a, b) => b.openingCount - a.openingCount)
      .map((group, index) => {
        const averageCentroid = group.centroidSum.multiplyScalar(1 / group.centroidCount)
        // Project the averaged centroid back onto the group plane so the point sits exactly on it.
        const distanceToPlane = group.normal.dot(averageCentroid) - group.offset
        const point = averageCentroid.sub(group.normal.multiplyScalar(distanceToPlane))

        return {
          id: `opening-plane-${index}`,
          normal: group.normal,
          point,
          wallGuids: group.wallGuids,
          openingCount: group.openingCount,
          frame: computePlaneFrame(group),
        }
      })
  )
}

/**
 * Builds the camera framing for a plane group: screen-up is world-up projected into the plane
 * (walls are vertical, so the plane normal is near-horizontal), screen-right completes the basis
 * such that `right × up` equals the plane normal — the camera then looks at the plane from the
 * normal side. Width/height are the extents of the group's wall points along these axes.
 */
const computePlaneFrame = (group: WorkingPlaneGroup): DetectedOpeningPlane['frame'] => {
  const upReference =
    Math.abs(group.normal.z) < 0.9 ? new ImmutableVector3(0, 0, 1) : new ImmutableVector3(1, 0, 0)
  const up = upReference.sub(group.normal.multiplyScalar(upReference.dot(group.normal))).normalize()
  const right = up.cross(group.normal).normalize()

  let minRight = Infinity
  let maxRight = -Infinity
  let minUp = Infinity
  let maxUp = -Infinity
  group.points.forEach(p => {
    const r = p.dot(right)
    const u = p.dot(up)
    minRight = Math.min(minRight, r)
    maxRight = Math.max(maxRight, r)
    minUp = Math.min(minUp, u)
    maxUp = Math.max(maxUp, u)
  })

  const offset = (group.minOffset + group.maxOffset) / 2
  const center = right
    .multiplyScalar((minRight + maxRight) / 2)
    .add(up.multiplyScalar((minUp + maxUp) / 2))
    .add(group.normal.multiplyScalar(offset))

  return { center, right, up, width: maxRight - minRight, height: maxUp - minUp }
}
